import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Router } from '@angular/router';
import { setCssAttributes } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';

import { StyleServiceService } from '@app/finxone-web-frontend/app/lib/services/style-service/style-service.service';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { ProjectSettingsState } from '@app/finxone-web-frontend/app/lib/state/project-settings.state';
import {
  noNumbersValidator,
  noSpecialCharsValidator,
} from '@app/finxone-web-frontend/app/lib/utils/validators/validators.utils';
import {
  FormFieldConfig,
  PaymentProvider,
  ProfileStateModel,
  ProviderClass,
  UiZoneWidgetConfig,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { ConfigService } from '../../../../../lib/services/config-service/config-service.service';
import { PageConfigurationProvider } from '../../../../zones/page-configuration.provider';
import { ZonePageUrlProvider } from '../../../../zones/zone-url-provider';
import { defaultValidationMessages } from '../../../input-field-widget/default-validation-messages';
import { FormSubmitter } from '../form-submitter/form-submitter.component';
import { PARENT_FORM_ROW_GAP } from '@app/finxone-web-frontend/app/lib/utils/di-tokens';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-name-widget',
  templateUrl: './name-widget.component.html',
  styleUrls: ['./name-widget.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class NameWidgetComponent extends FormSubmitter implements OnInit {
  @AutoDestroy override destroy$: Subject<void> = new Subject<void>();
  public widgetConfig: UiZoneWidgetConfig;
  public totalSteps: number;
  public userProfileData: ProfileStateModel;
  public isStepComplete = false;
  public isError = false;
  public borderStyle: baseCssConfigStyle;
  public isNameShowSuccessIcon = false;
  public isNameShowErrorIcon = false;
  public fieldAttributes: any[];
  public formId: string;
  public nameForm: FormGroup<{ [key: string]: FormControl }>;
  public isValueChanged = false;
  public disableAutocomplete = false;
  public validationMessages = defaultValidationMessages;

  public parentFormWidgetRowGap: string | undefined | null = inject(PARENT_FORM_ROW_GAP, { optional: true });

  constructor(
    private configService: ConfigService,
    public dataIn: ProviderClass<any>,
    protected override store: Store,
    protected _router: Router,
    private styleService: StyleServiceService,
    public pageConfig: PageConfigurationProvider,
    private workflowFormsService: WorkflowFormsService,
    private zonePageUrlProvider: ZonePageUrlProvider,
  ) {
    super(store);
    if (this.dataIn != undefined) {
      this.widgetConfig = this.dataIn.widgetConfig;
      this.fieldAttributes = this.widgetConfig?.attributes['field_attributes'];
      this.attri = this.dataIn.attri;
    }
  }

  ngOnInit(): void {
    this.formId = this.dataIn?.attri?.widgetProperties?.parentForm;

    if (this.dataIn != undefined) {
      this.widgetConfig = this.dataIn.widgetConfig;
      this.attri = this.dataIn.attri;
      // Should disable auto complete on the form field in the browser
      this.disableAutocomplete =
        this.widgetConfig.attributes.widgetProperties?.['disableAutocomplete'] ?? false;
    }

    this.buildForm();
    this.setCssAttributes();
    this.checkNameSuccessErrorIconFlag();

    if (this.formId) {
      const form = this.workflowFormsService.getFormPart(this.formId);
      if (form) {
        form.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {
          this.updateStepValidity();
        });
      }
    }
  }

  private updateStepValidity(): void {
    const firstNameCtrl = this.nameForm?.get('firstName');
    const lastNameCtrl = this.nameForm?.get('lastName');
    const firstNameInvalid = firstNameCtrl?.invalid && firstNameCtrl?.dirty;
    const lastNameInvalid = lastNameCtrl?.invalid && lastNameCtrl?.dirty;

    // isStepComplete valid only when both valid
    this.isStepComplete = !!(firstNameCtrl?.valid && lastNameCtrl?.valid);

    // error is true if either invalid
    this.isError = !!(firstNameInvalid || lastNameInvalid);

    this.checkNameSuccessErrorIconFlag();
  }

  private checkNameSuccessErrorIconFlag() {
    if (this.dataIn && this.widgetConfig) {
      const showSuccessErrorIcon = this.isShowAndErorIcon(this.widgetConfig.attributes['elementsConfig']);
      this.isNameShowErrorIcon = showSuccessErrorIcon.isShowErrorIcon;
      this.isNameShowSuccessIcon = showSuccessErrorIcon.isShowSuccessIcon;
    }
  }

  public setCssAttributes() {
    const { contentStyle, borderStyle, textInputFontStyle, textInputBackgroundColor } = setCssAttributes(
      this.styleService,
      this.configService,
      this.attri,
    );

    this.contentStyle = contentStyle;
    this.borderStyle = borderStyle;
  }

  private buildForm() {
    if (!this.formId) return;

    const formConfig: FormFieldConfig[] = (this.fieldAttributes || []).map((field) => {
      return {
        id: field.name,
        type: field.type,
        label: field.label || field.name,
        parentForm: this.formId,
        name: field.name,
        validation: {
          required: field.decision_config?.required ?? false,
          customValidator: this.combinedNameValidator(),
        },
      };
    });
    const formId = this.workflowFormsService.generateCustomFormId(this.formId, this.zonePageUrlProvider);
    this.nameForm = this.workflowFormsService.getFormPart(formId) ?? new FormGroup({});
    this.workflowFormsService.addFormControl(this.nameForm, formConfig);
    const stateValues =
      this.store.selectSnapshot(FormActionState.getFormActionStateWithId(this.formId)) ?? {};

    if (
      stateValues?.formData &&
      (stateValues?.formData.firstName?.trim() || stateValues?.formData?.lastName?.trim())
    ) {
      this.nameForm.patchValue(stateValues?.formData);
      this.updateStepValidity();
    }
  }

  public combinedNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const errors: ValidationErrors = {};
      // remove the custom validation when provider is thisbank
      if (this.isThisBankProvider()) {
        return null;
      }
      const numberError = noNumbersValidator()(control);
      const specialCharError = noSpecialCharsValidator()(control);
      if (numberError) {
        errors['noNumbers'] = numberError['noNumbers'];
      }
      if (specialCharError) {
        errors['noSpecialChars'] = specialCharError['noSpecialChars'];
      }
      return Object.keys(errors).length ? errors : null;
    };
  }

  private isThisBankProvider = () =>
    this.store.selectSnapshot(ProjectSettingsState.getProjectServiceProvider) === PaymentProvider.THISBANK;
}
